// =============================================================================
// REGIONAL DATA - Air Traffic Control for National Operations
// =============================================================================

import { Region, RegionId, RegionalContacts, Supplier, Installer } from '../types/common';
import { Order } from '../types/orders';
import { operationsUrgency } from '../../shared/operationsOrderStatus';

const REGIONS_STORAGE_KEY = 'bliss_regional_contacts';

// 9 US Regions with state mappings
export const REGIONS: Record<RegionId, Region> = {
  'new-england': {
    id: 'new-england',
    name: 'New England',
    shortName: 'NE',
    states: ['ME', 'NH', 'VT', 'MA', 'RI', 'CT'],
    color: '#4A90A4',
    timezone: 'America/New_York',
    majorCities: ['Boston', 'Providence', 'Hartford']
  },
  'mid-atlantic': {
    id: 'mid-atlantic', 
    name: 'Mid-Atlantic',
    shortName: 'MA',
    states: ['NY', 'NJ', 'PA', 'DE', 'MD', 'DC'],
    color: '#7B68EE',
    timezone: 'America/New_York',
    majorCities: ['New York', 'Philadelphia', 'Baltimore']
  },
  'southeast': {
    id: 'southeast',
    name: 'Southeast', 
    shortName: 'SE',
    states: ['VA', 'WV', 'NC', 'SC', 'GA', 'FL', 'AL', 'MS', 'TN', 'KY'],
    color: '#F4A460',
    timezone: 'America/New_York',
    majorCities: ['Atlanta', 'Miami', 'Charlotte', 'Nashville']
  },
  'midwest': {
    id: 'midwest',
    name: 'Midwest',
    shortName: 'MW',
    states: ['OH', 'IN', 'IL', 'MI', 'WI', 'MN'],
    color: '#20B2AA',
    timezone: 'America/Chicago',
    majorCities: ['Chicago', 'Detroit', 'Minneapolis', 'Indianapolis']
  },
  'great-plains': {
    id: 'great-plains',
    name: 'Great Plains',
    shortName: 'GP',
    states: ['ND', 'SD', 'NE', 'KS', 'MO', 'IA'],
    color: '#DAA520',
    timezone: 'America/Chicago',
    majorCities: ['Kansas City', 'St. Louis', 'Omaha']
  },
  'southwest': {
    id: 'southwest',
    name: 'Southwest',
    shortName: 'SW',
    states: ['TX', 'OK', 'AR', 'LA', 'NM', 'AZ'],
    color: '#CD853F',
    timezone: 'America/Chicago',
    majorCities: ['Houston', 'Dallas', 'Phoenix', 'San Antonio']
  },
  'mountain': {
    id: 'mountain',
    name: 'Mountain',
    shortName: 'MT',
    states: ['MT', 'ID', 'WY', 'CO', 'UT', 'NV'],
    color: '#708090',
    timezone: 'America/Denver',
    majorCities: ['Denver', 'Salt Lake City', 'Las Vegas']
  },
  'pacific-northwest': {
    id: 'pacific-northwest',
    name: 'Pacific Northwest',
    shortName: 'PNW',
    states: ['WA', 'OR', 'AK'],
    color: '#2E8B57',
    timezone: 'America/Los_Angeles',
    majorCities: ['Seattle', 'Portland', 'Anchorage']
  },
  'california': {
    id: 'california',
    name: 'California',
    shortName: 'CA',
    states: ['CA', 'HI'],
    color: '#FF6347',
    timezone: 'America/Los_Angeles',
    majorCities: ['Los Angeles', 'San Francisco', 'San Diego', 'Honolulu']
  },
  'unknown': {
    id: 'unknown',
    name: 'Unassigned region',
    shortName: 'N/A',
    states: [],
    color: '#999999',
    timezone: 'America/New_York',
    majorCities: []
  }
};

// Get region ID from state abbreviation
export function getRegionFromState(stateAbbr: string): RegionId {
  if (!stateAbbr) return 'unknown';
  const upperState = stateAbbr.toUpperCase().trim();
  
  for (const [regionId, region] of Object.entries(REGIONS)) {
    if (region.states.includes(upperState)) {
      return regionId as RegionId;
    }
  }
  return 'unknown';
}

// Get region data by ID
export function getRegionById(regionId: string): Region | null {
  return REGIONS[regionId as RegionId] || null;
}

// Get all regions as array
export function getAllRegions(): Region[] {
  return Object.values(REGIONS).filter(r => r.id !== 'unknown');
}

// =============================================================================
// REGIONAL CONTACTS - Suppliers and Installers
// =============================================================================

// Default placeholder contacts (user will add their own)
const DEFAULT_CONTACTS: Record<string, RegionalContacts> = {
  'new-england': {
    suppliers: [],
    installers: []
  },
  'mid-atlantic': {
    suppliers: [],
    installers: []
  },
  'southeast': {
    suppliers: [],
    installers: []
  },
  'midwest': {
    suppliers: [],
    installers: []
  },
  'great-plains': {
    suppliers: [],
    installers: []
  },
  'southwest': {
    suppliers: [],
    installers: []
  },
  'mountain': {
    suppliers: [],
    installers: []
  },
  'pacific-northwest': {
    suppliers: [],
    installers: []
  },
  'california': {
    suppliers: [],
    installers: []
  }
};

// In-memory cache for regional contacts (seeded with defaults)
let regionalContactsCache: Record<string, RegionalContacts> = { ...DEFAULT_CONTACTS }

// Get regional contacts (session-only)
export function getRegionalContacts(): Record<string, RegionalContacts> {
  return regionalContactsCache
}

// Save regional contacts (session-only)
export function saveRegionalContacts(contacts: Record<string, RegionalContacts>): void {
  regionalContactsCache = contacts
}

// Get contacts for a specific region
export function getContactsForRegion(regionId: string): RegionalContacts {
  const contacts = getRegionalContacts();
  return contacts[regionId] || { suppliers: [], installers: [] };
}

// Add a supplier to a region
export function addSupplierToRegion(regionId: string, supplier: Partial<Supplier>): Supplier {
  const contacts = getRegionalContacts();
  if (!contacts[regionId]) {
    contacts[regionId] = { suppliers: [], installers: [] };
  }
  
  const newSupplier: Supplier = {
    id: `sup-${Date.now()}`,
    name: supplier.name || '',
    phone: supplier.phone || '',
    email: supplier.email || '',
    city: supplier.city || '',
    state: supplier.state || '',
    address: supplier.address || '',
    notes: supplier.notes || '',
    specialties: supplier.specialties || [],
    addedAt: new Date().toISOString()
  };
  
  contacts[regionId].suppliers.push(newSupplier);
  saveRegionalContacts(contacts);
  return newSupplier;
}

// Add an installer to a region
export function addInstallerToRegion(regionId: string, installer: Partial<Installer>): Installer {
  const contacts = getRegionalContacts();
  if (!contacts[regionId]) {
    contacts[regionId] = { suppliers: [], installers: [] };
  }
  
  const newInstaller: Installer = {
    id: `ins-${Date.now()}`,
    name: installer.name || '',
    phone: installer.phone || '',
    email: installer.email || '',
    city: installer.city || '',
    state: installer.state || '',
    franchiseId: installer.franchiseId || '',
    notes: installer.notes || '',
    serviceRadius: installer.serviceRadius || 50,
    addedAt: new Date().toISOString()
  };
  
  contacts[regionId].installers.push(newInstaller);
  saveRegionalContacts(contacts);
  return newInstaller;
}

// Update a contact
export function updateContact(
  regionId: string, 
  contactType: 'supplier' | 'installer', 
  contactId: string, 
  updates: Partial<Supplier | Installer>
): Supplier | Installer | null {
  const contacts = getRegionalContacts();
  if (!contacts[regionId]) return null;
  
  const list = contactType === 'supplier' ? contacts[regionId].suppliers : contacts[regionId].installers;
  const index = list.findIndex(c => c.id === contactId);
  
  if (index >= 0) {
    list[index] = { ...list[index], ...updates, updatedAt: new Date().toISOString() };
    saveRegionalContacts(contacts);
    return list[index];
  }
  return null;
}

// Delete a contact
export function deleteContact(regionId: string, contactType: 'supplier' | 'installer', contactId: string): boolean {
  const contacts = getRegionalContacts();
  if (!contacts[regionId]) return false;
  
  if (contactType === 'supplier') {
    contacts[regionId].suppliers = contacts[regionId].suppliers.filter(c => c.id !== contactId);
  } else {
    contacts[regionId].installers = contacts[regionId].installers.filter(c => c.id !== contactId);
  }
  
  saveRegionalContacts(contacts);
  return true;
}

// =============================================================================
// REGIONAL ORDER HELPERS
// =============================================================================

// Group orders by region
export function groupOrdersByRegion(orders: Order[]): Record<string, Order[]> {
  const grouped: Record<string, Order[]> = {};
  
  // Initialize all regions
  Object.keys(REGIONS).forEach(regionId => {
    grouped[regionId] = [];
  });
  grouped['unknown'] = [];
  
  // Assign orders to regions
  orders.forEach(order => {
    const state = order.client?.state || '';
    const regionId = getRegionFromState(state);
    grouped[regionId].push(order);
  });
  
  return grouped;
}

interface RegionalSummary {
  regionId: string;
  region: Region;
  orderCount: number;
  urgentCount: number;
  overdueCount: number;
  orders: Order[];
  supplierCount: number;
  installerCount: number;
}

// Get regional summary statistics
export function getRegionalSummary(orders: Order[]): RegionalSummary[] {
  const grouped = groupOrdersByRegion(orders);
  
  return Object.entries(REGIONS)
    .filter(([regionId]) => regionId !== 'unknown' || grouped.unknown.length > 0)
    .map(([regionId, region]) => {
      const regionOrders = grouped[regionId] || [];
      const contacts = getContactsForRegion(regionId);
      
      // Count urgent orders
      const now = Date.now();
      const urgentOrders = regionOrders.filter(o => ['urgent', 'soon'].includes(operationsUrgency(o, now)));
      
      // Count overdue orders
      const overdueOrders = regionOrders.filter(o => operationsUrgency(o, now) === 'overdue');
      
      return {
        regionId,
        region,
        orderCount: regionOrders.length,
        urgentCount: urgentOrders.length,
        overdueCount: overdueOrders.length,
        orders: regionOrders,
        supplierCount: contacts.suppliers.filter(s => s.name && s.name !== 'Add Supplier').length,
        installerCount: contacts.installers.filter(i => i.name && i.name !== 'Add Installer').length
      };
    });
}

// Export for use in other modules
export default {
  REGIONS,
  getRegionFromState,
  getRegionById,
  getAllRegions,
  getRegionalContacts,
  saveRegionalContacts,
  getContactsForRegion,
  addSupplierToRegion,
  addInstallerToRegion,
  updateContact,
  deleteContact,
  groupOrdersByRegion,
  getRegionalSummary
};

